import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import TemplateForm from "@pages/Template/components/TemplateForm";
import FileUpload from "@pages/Template/components/FileUpload";
import TemplateGeneration from "@pages/Template/components/TemplateGeneration";
import useHandleSaveTemplate from "@pages/Template/utils/useHandleSaveTemplate";
import { useForm, FormProvider } from "react-hook-form";
import { useUnsavedChangesWarning } from "@shared/components/UnsavedChangesPopup/useUnsavedChangesWarning";
import UnsavedChangesModal from "@shared/components/UnsavedChangesPopup/UnsavedChangesModal";
import { useUserService } from "@utils/useUsers";
import { TemplateFormData } from "@shared/interfaces/Template";
import NewItemButton from "@shared/components/NewItemButton";

const CreateTemplatePage: React.FC = () => {
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const location = useLocation();
  const { reportName, projectId, checkedItems, isChoosing, returnUrl } =
    location.state || {};
  const [isTemplateGenerating, setIsTemplateGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const [isGenerated, setIsGenerated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { getCurrentUser } = useUserService();

  const methods = useForm({
    mode: "all", // ✅ Show errors immediately when the user types
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      templateName: "",
      templateDescription: "",
      templatePrompt: "",
      templateType: "",
    },
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { isValid }, // ✅ Extract isValid
  } = methods;
  const { watch } = methods;
  const templatePrompt = watch("templatePrompt");
  watch("templateName");
  watch("templateDescription");
  useEffect(() => {
    const fetchCompanyId = async () => {
      try {
        const user = await getCurrentUser();
        const companyId = user?.company_relation?.company?.id ?? null;
        setCompanyId(companyId);
      } catch (error) {
        console.error("Error fetching company ID:", error);
      }
    };
    fetchCompanyId();
  }, []);

  const handleSaveTemplate = useHandleSaveTemplate(
    {
      isChoosing,
      projectId,
      reportName,
      checkedItems,
      returnUrl,
    },
    methods
  );

  useEffect(() => {
    if (!templatePrompt) {
      setIsGenerated(false);
    }
  }, [templatePrompt]);

  const onSave = async (values: TemplateFormData) => {
    if (!isValid) return;

    try {
      await handleSaveTemplate(
        {
          name: values.templateName,
          description: values.templateDescription,
          template_type: values.templateType,
          prompt: values.templatePrompt,
          company_id: companyId,
        },
        setSubmitting
      );
    } catch (error) {
      console.error("Error saving template:", error);
    }
  };

  // blocker is a function that will block navigation if there are unsaved changes
  const { blocker, handleProceed, handleCancel, isBlocked } =
    useUnsavedChangesWarning(
      getValues(), // current form values
      methods.control._defaultValues // initial/default values
    );

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal
        onProceed={handleProceed}
        onCancel={handleCancel}
        isOpen={isBlocked}
      />
      <form
        onSubmit={handleSubmit(onSave)}
        className="w-full max-h-screen flex flex-col relative"
      >
        <div className="flex flex-col min-h-[90vh] relative">
          {/* Main Content */}
          <div className="flex flex-1 px-8 ">
            {/* Left Content */}
            <div className="flex-1 flex flex-col">
              <SubTitleHeader
                spacer={true}
                children={
                  <NewItemButton
                    showIcon={false}
                    type="submit"
                    title="Create"
                    disabled={!isValid || submitting}
                    isLoading={submitting}
                  />
                }
                title="Create Template"
              />

              <div className="flex flex-1 pt-5">
                {/* Template Form Section */}
                <div className="flex-1 ">
                  <TemplateForm isEditing={!submitting}></TemplateForm>
                </div>

                {/* Right-Side Panel */}
                <div className="w-[30%] pt-0 p-4 pr-0 ml-6 mt-3">
                  <h2 className="  mb-3 text-gray-700">
                    Use a Previous Report
                  </h2>
                  <div className="h-[calc(100%-50px)] bg-gray-100 rounded-lg  flex flex-col items-center justify-center">
                    {!isTemplateGenerating && (
                      <FileUpload
                        onTemplateGenerated={setTaskId}
                        setIsTemplateGenerating={setIsTemplateGenerating}
                        isEditing={true}
                      />
                    )}

                    <TemplateGeneration
                      taskId={taskId}
                      isTemplateGenerating={isTemplateGenerating}
                      setIsTemplateGenerating={setIsTemplateGenerating}
                      setIsGenerated={setIsGenerated}
                      setTemplatePrompt={(prompt: string | null) => {
                        setValue("templatePrompt", prompt ?? "", {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                      setGenerationStatus={setGenerationStatus}
                      generationStatus={generationStatus}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateTemplatePage;
